/**
 * Quick-checkout cart action builders — the boundary between the cart
 * dismiss / rollback UI paths and the cart HTTP routes. Four thin async
 * functions:
 *   - `releaseCart` — drop the buyer's reservations via fetch
 *   - `releaseCartViaBeacon` — same, but synchronous, for unload contexts
 *     where `fetch` is cancelled by the browser
 *   - `removeListingFromCart` — undo cart additions for a listing (GET +
 *     DELETE each row)
 *   - `deleteCartItem` — direct DELETE by `cart_items.id` (no preceding GET);
 *     used by the master cart UI where the row id is already known
 *
 * Each owns its `fetch`(es), runs its `@kraahl/contract` Zod schema before
 * the network call, and returns a minimal typed result.
 *
 *   components/chat/GlobalQuickCheckout.tsx ─┐
 *   components/chat/ChatCheckoutDialog.tsx   ├─▶ releaseCart / removeListingFromCart
 *   components/cart/cart.tsx                 │   releaseCartViaBeacon
 *   components/cart/cart-provider.tsx        ┘   deleteCartItem
 *                                                          │
 *                                                          ├── safeParse (boundary)
 *                                                          └── fetch / sendBeacon + return
 *
 * Why this exists:
 *  - Inline `fetch('/api/cart/release')` and a GET-then-DELETE-each-row
 *    inside large client components made the request shape implicit,
 *    duplicated across files, and impossible to component-test without
 *    spinning up the entire dialog tree. See FRONTEND_WIRING_STRATEGY.md
 *    §3 (Layer 3 — wiring) and §4 #2-3 (pre-lock extraction targets).
 *  - The same dismiss-and-rollback flow exists on mobile (when chat quick
 *    checkout lands there). Sharing a builder via `@kraahl/contract` Zod
 *    types means web + mobile cannot drift on the wire.
 *  - Component tests can now mock `@/lib/cart-quick-checkout` and assert
 *    "close click → releaseCart called with X" without faking `fetch`.
 *
 * Design notes:
 *  - Builders accept INTENT-style args (caller naturally has `listingIds` +
 *    `userId` from cart/auth state), not the raw wire body. Assembly is the
 *    builder's job.
 *  - Schema parse failures THROW (`CartActionError`). Callers that want
 *    fire-and-forget semantics (e.g. cleanup on sheet dismiss) wrap the
 *    call in `.catch(() => {})` — same pattern as the inline fetches they
 *    replace.
 *  - Response shape is NOT pinned by Zod. Same precedent as
 *    `lib/payments/checkout-actions.ts` — response-side schemas are a
 *    separate iteration.
 *  - `releaseCartViaBeacon` is intentionally a separate function (not a
 *    flag on `releaseCart`): `navigator.sendBeacon` is synchronous, returns
 *    `boolean` (queued / refused), and is the only transport that survives
 *    `pagehide`/`beforeunload` — `fetch` is aborted by the browser there.
 *    The two transports are asymmetric in signature and failure mode; a
 *    union return type would lose precision at every call site.
 *
 * @see .tondocs/FRONTEND_WIRING_STRATEGY.md §4 #2 — this is items #2-3
 * @see .tondocs/skills/hex.md §2 — Functional Core / Imperative Shell
 * @see packages/contract/src/release-cart.ts — ReleaseCartRequest contract
 * @see packages/contract/src/delete-cart-item.ts — DeleteCartItemRequest contract
 * @see lib/payments/checkout-actions.ts — sibling builder cluster (PR #242)
 */

import {
  DeleteCartItemRequestSchema,
  ReleaseCartRequestSchema,
  type DeleteCartItemRequest,
  type ReleaseCartRequest,
} from "@kraahl/contract";

// ---------------------------------------------------------------------------
// Shared error
// ---------------------------------------------------------------------------

/**
 * Thrown when a request body fails the contract Zod parse before fetch. This
 * is a programmer-bug signal: the caller assembled args the contract does
 * not accept. The error message includes the route path so the failure is
 * greppable; `issues` carries Zod's issue list for the responsible developer
 * (NOT shown to the buyer).
 */
export class CartActionError extends Error {
  readonly route: string;
  readonly issues: ReadonlyArray<{ path: string; message: string }>;
  constructor(
    route: string,
    issues: ReadonlyArray<{ path: string; message: string }>,
  ) {
    super(
      `[cart-quick-checkout] contract parse failed for ${route}: ${issues
        .map((i) => `${i.path}: ${i.message}`)
        .join("; ")}`,
    );
    this.name = "CartActionError";
    this.route = route;
    this.issues = issues;
  }
}

// ---------------------------------------------------------------------------
// G5a / G7 — releaseCart
// ---------------------------------------------------------------------------

export interface ReleaseCartArgs {
  /**
   * Listing ids the buyer was reserving. Forwarded into the server's
   * `inventory` broadcast so other tabs/clients refresh availability for
   * the same listings.
   *
   * Note: the server deletes ALL `item_reservations` rows for the
   * authenticated user regardless of this list — the field is broadcast
   * scope, not a delete filter. Pass the ids the user was actively viewing.
   */
  listingIds?: string[];
}

export interface ReleaseCartResult {
  /** `true` when the server-side release succeeded. */
  ok: boolean;
}

/**
 * Build the release-cart request, schema-check it, POST it. Returns
 * `{ ok: response.ok }`. Network failures throw — callers that want
 * fire-and-forget semantics (sheet-dismiss cleanup) wrap with `.catch(...)`.
 */
export async function releaseCart(
  args: ReleaseCartArgs = {},
): Promise<ReleaseCartResult> {
  const body: ReleaseCartRequest = {
    ...(args.listingIds ? { listingIds: args.listingIds } : {}),
  };

  const parsed = ReleaseCartRequestSchema.safeParse(body);
  if (!parsed.success) {
    throw new CartActionError(
      "/api/cart/release",
      parsed.error.issues.map((i) => ({
        path: i.path.join("."),
        message: i.message,
      })),
    );
  }

  const response = await fetch("/api/cart/release", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });

  return { ok: response.ok };
}

// ---------------------------------------------------------------------------
// G13 — releaseCartViaBeacon (synchronous, unload-safe)
// ---------------------------------------------------------------------------

export interface ReleaseCartViaBeaconResult {
  /**
   * Whether the browser accepted the beacon for delivery. `false` means
   * `sendBeacon` is unavailable (SSR / very old browser) or the user-agent
   * refused (rare — typically a payload-size limit, not relevant here).
   * NEVER use this to infer server-side success — the beacon is fire-and-
   * forget by design.
   */
  queued: boolean;
}

/**
 * Same wire shape as `releaseCart` but via `navigator.sendBeacon`. The only
 * transport that survives `pagehide` / `beforeunload` / `visibilitychange`
 * → "hidden" — `fetch` is aborted by the browser there. Synchronous: returns
 * the queued-boolean directly, no promise to await before the page tears down.
 */
export function releaseCartViaBeacon(
  args: ReleaseCartArgs = {},
): ReleaseCartViaBeaconResult {
  const body: ReleaseCartRequest = {
    ...(args.listingIds ? { listingIds: args.listingIds } : {}),
  };

  const parsed = ReleaseCartRequestSchema.safeParse(body);
  if (!parsed.success) {
    throw new CartActionError(
      "/api/cart/release",
      parsed.error.issues.map((i) => ({
        path: i.path.join("."),
        message: i.message,
      })),
    );
  }

  if (typeof navigator === "undefined" || typeof navigator.sendBeacon !== "function") {
    return { queued: false };
  }

  const blob = new Blob([JSON.stringify(body)], { type: "application/json" });
  return { queued: navigator.sendBeacon("/api/cart/release", blob) };
}

// ---------------------------------------------------------------------------
// G5b — removeListingFromCart (GET + DELETE composition)
// ---------------------------------------------------------------------------

export interface RemoveListingFromCartArgs {
  /** Authenticated buyer. Used both for the GET filter and the DELETE auth check. */
  userId: string;
  /** `listings.id` whose cart rows should be removed. */
  listingId: string;
}

/**
 * Minimal subset of the `GET /api/cart` enriched response that this builder
 * consumes — only the fields needed to find matching rows for a listing.
 * The route returns much more (the full enriched cart); we ignore the rest
 * to avoid coupling this builder to a heavy response shape.
 */
interface MinimalCartRow {
  id: string;
  listing_id?: string;
}

/**
 * Undo cart additions for a given listing in the buyer's cart. Used when the
 * buyer dismisses the quick-checkout sheet after having added the listing in
 * that same flow — the canonical "rollback what we did" path.
 *
 * Composition (matches the pre-extract inline code):
 *   1. GET `/api/cart?userId=<id>` to enumerate cart rows for this listing.
 *   2. For each match, DELETE `/api/cart` with `{ id, userId }`.
 *
 * Deletes are serial to mirror the original inline behaviour (typical cart
 * has 1-2 rows per listing — parallelism is not the bottleneck). Throws on
 * first network failure; caller wraps with `.catch(...)` for best-effort
 * cleanup semantics.
 */
export async function removeListingFromCart(
  args: RemoveListingFromCartArgs,
): Promise<void> {
  const res = await fetch(
    `/api/cart?userId=${encodeURIComponent(args.userId)}`,
    { headers: { "Cache-Control": "no-cache" } },
  );
  const rows = (res.ok ? ((await res.json()) as MinimalCartRow[]) : []);
  const matchingCartIds = rows
    .filter((r) => String(r?.listing_id ?? "") === args.listingId)
    .map((r) => r.id);

  for (const cartId of matchingCartIds) {
    await deleteCartItem({ id: cartId, userId: args.userId });
  }
}

// ---------------------------------------------------------------------------
// G15 — deleteCartItem (direct DELETE by cart_items.id, no preceding GET)
// ---------------------------------------------------------------------------

export interface DeleteCartItemArgs {
  /** `cart_items.id` to delete. Required. */
  id: string;
  /**
   * Authenticated buyer id. Mutually exclusive with `guestId` at the wire
   * level (XOR — pass exactly one; if your source is `string | null`,
   * coalesce with `?? undefined` so the builder can omit the absent side).
   */
  userId?: string;
  /** Guest cart id when the buyer is not authenticated. */
  guestId?: string;
  /**
   * Cancel the in-flight DELETE. Threaded through for the master cart UI
   * which wraps every mutation in `enqueueMutation` + `AbortController` +
   * optimistic update + tombstone.
   */
  signal?: AbortSignal;
}

export interface DeleteCartItemResult {
  /** `true` when the server returned 2xx. */
  ok: boolean;
}

/**
 * Thin DELETE builder for `cart_items.id`. Used by both the master cart UI
 * (where the row id is already known from local state) and
 * `removeListingFromCart` under the hood (one fetch per id after the GET).
 *
 * Unlike `removeListingFromCart`, this does NOT enumerate the cart first —
 * the caller already has the id. Behaviour for missing row is delegated to
 * the route (which currently 2xx's the no-op).
 */
export async function deleteCartItem(
  args: DeleteCartItemArgs,
): Promise<DeleteCartItemResult> {
  const body: DeleteCartItemRequest = {
    id: args.id,
    ...(args.userId !== undefined ? { userId: args.userId } : {}),
    ...(args.guestId !== undefined ? { guestId: args.guestId } : {}),
  };

  const parsed = DeleteCartItemRequestSchema.safeParse(body);
  if (!parsed.success) {
    throw new CartActionError(
      "/api/cart",
      parsed.error.issues.map((i) => ({
        path: i.path.join("."),
        message: i.message,
      })),
    );
  }

  const response = await fetch("/api/cart", {
    method: "DELETE",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
    signal: args.signal,
  });

  return { ok: response.ok };
}
